import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { HomeOutlined, UserOutlined } from "@ant-design/icons";
import { useQueryClient } from "@tanstack/react-query";
import { Breadcrumb, Form, Spin } from "antd";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ContentBlockerTemplateForm } from "./contentBlockerTemplateForm.js";
import { useContentBlockerTemplateByIdentifier } from "../../../../../hooks/content-blocker/useContentBlockerTemplate.js";
import { useContentBlockerTemplateEdit } from "../../../../../hooks/content-blocker/useContentBlockerTemplateEdit.js";
import { useFormValidation } from "../../../../../hooks/form/useFormValidation.js";
import { useTranslation } from "../../../../../others/i18n.js";
import { useStores } from "../../../../../store/root.js";
import { QueryKeys } from "../../../../../util/query-keys.js";
function ContentBlockerTemplateEdit(props) {
    const { t } = useTranslation();
    const { notificationStore } = useStores();
    const queryClient = useQueryClient();
    const { modalModeOptions } = props;
    let { identifier } = useParams();
    identifier = identifier || modalModeOptions?.editIdentifier;
    const navigate = useNavigate();
    const [templateForm] = Form.useForm();
    const [validationErrors, setValidationErrors] = useState(undefined);
    useFormValidation(templateForm, validationErrors);
    const [contentBlockerTemplate, setContentBlockerTemplate] = useState(undefined);
    const useContentBlockerTemplate = useContentBlockerTemplateByIdentifier(identifier);
    useEffect(()=>{
        // definitely reload template (could be updated due to parent updates)
        queryClient.invalidateQueries(QueryKeys.getContentBlockerByIdentifier(identifier));
    }, [
        identifier
    ]);
    useEffect(()=>{
        const { data, isError, isFetched, isSuccess } = useContentBlockerTemplate;
        if (isFetched) {
            if (isSuccess && data) {
                setContentBlockerTemplate(data);
            } else if (isError) {
                navigate("/rcb/content-blocker");
            }
        }
    }, [
        useContentBlockerTemplate
    ]);
    const [contentBlockerTemplateToEdit, setContentBlockerTemplateToEdit] = useState(undefined);
    const useUpdateContentBlockerTemplate = useContentBlockerTemplateEdit(contentBlockerTemplateToEdit);
    useEffect(()=>{
        const { data, error, isError, isFetching, isSuccess, isLoading } = useUpdateContentBlockerTemplate;
        if (isFetching || isLoading) {
            return;
        }
        if (isSuccess && data && !isNaN(Number(data.id))) {
            notificationStore.clearAll();
            setContentBlockerTemplateToEdit(undefined);
            setValidationErrors(undefined);
            setContentBlockerTemplate(undefined);
            useContentBlockerTemplate.refetch();
            notificationStore.add({
                type: "success",
                message: t("Template updated!"),
                closable: true,
                duration: 3
            });
            modalModeOptions?.onAfterSave?.();
        } else if (isError && error) {
            notificationStore.clearAll();
            setValidationErrors(error);
            notificationStore.addFromServerError(error, t);
        }
    }, [
        useUpdateContentBlockerTemplate,
        contentBlockerTemplateToEdit
    ]);
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsx(Breadcrumb, {
                items: [
                    {
                        href: "/rcb",
                        title: /*#__PURE__*/ _jsx(HomeOutlined, {})
                    },
                    {
                        href: "/rcb/content-blocker",
                        title: /*#__PURE__*/ _jsxs(_Fragment, {
                            children: [
                                /*#__PURE__*/ _jsx(UserOutlined, {}),
                                /*#__PURE__*/ _jsx("span", {
                                    children: t("Content Blocker")
                                })
                            ]
                        })
                    },
                    {
                        title: t("Edit")
                    }
                ]
            }),
            contentBlockerTemplate ? /*#__PURE__*/ _jsx(ContentBlockerTemplateForm, {
                isLoading: useUpdateContentBlockerTemplate.isFetching,
                contentBlockerTemplate: contentBlockerTemplate,
                templateForm: templateForm,
                onSubmit: (formTemplate, currentTemplate)=>{
                    formTemplate["_lastSubmit"] = new Date().getTime();
                    formTemplate.id = currentTemplate.id; // set here, because it's lost in form
                    setContentBlockerTemplateToEdit(formTemplate);
                },
                isModalView: !!modalModeOptions
            }) : /*#__PURE__*/ _jsx("div", {
                style: {
                    height: "800px",
                    width: "100%"
                },
                children: /*#__PURE__*/ _jsx(Spin, {
                    size: "large",
                    style: {
                        position: "fixed",
                        left: "50%",
                        top: "50%"
                    },
                    spinning: true
                })
            })
        ]
    });
}
export { ContentBlockerTemplateEdit };
